import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Input,
  message,
  Table,
  InputNumber,
  AutoComplete,
  Select,
} from "antd";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import styled from "styled-components";
import IntlCurrencyInput from "react-intl-currency-input";
import { fetchProductGetAll } from "../../view/product/action/action";
import { Box, Typography } from "@mui/material";
import FreightEditemodSuccessModaTest from "../../view/frete-copy/components/modalTeste";
import { productTableThemeVars } from "../../theme/globalStyle/globalStyle";

const currencyConfig = {
  locale: 'pt-BR',
  formats: {
    number: {
      BRL: {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

const weightConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "decimal",
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      },
    },
  },
};

const StyledButton = styled(Button)`
  && {
    background-color: #012443;
    border-radius: 8px;
    height: 40px;
    padding: 0 18px;
  }

  &:hover {
    background-color: #023d70ff !important;
  }
`;

const ProductTableShell = styled.div`
  color: var(--product-table-text-primary);

  .product-pricing-table .ant-table {
    background: var(--product-table-surface);
    border-radius: var(--product-table-radius-md);
  }

  .product-pricing-table .ant-table-thead > tr > th {
    background: var(--product-table-surface-soft);
    color: var(--product-table-text-primary);
    font-weight: 700;
    white-space: nowrap;
  }

  .product-pricing-table .ant-table-tbody > tr > td {
    vertical-align: top;
  }

  .product-pricing-table .ant-input,
  .product-pricing-table .ant-input-number,
  .product-pricing-table .ant-select,
  .product-pricing-table .form-control {
    width: 100%;
  }

  .product-pricing-table .ant-input,
  .product-pricing-table .ant-input-number,
  .product-pricing-table .ant-select-selector,
  .product-pricing-table .form-control {
    border-radius: var(--product-table-radius-sm) !important;
  }
`;

const ProductFieldCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const ProductFieldMeta = styled.div`
  color: var(--product-table-text-secondary);
  font-size: 12px;
  line-height: 16px;
  min-height: 16px;
`;

const ProductTotalValue = styled.span`
  align-items: center;
  color: var(--product-table-text-primary);
  display: flex;
  min-height: 32px;
`;

const TotalMetricGroup = styled.div`
  display: grid;
  gap: 6px;
  min-width: 0;
`;

const TotalMetricLabel = styled.span`
  color: #4b5563;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
`;

const TotalMetricValue = styled.span`
  color: #111827;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.15;
  white-space: nowrap;
`;

const TotalMetricValueSecondary = styled(TotalMetricValue)`
  color: #012443;
  font-size: 15px;
  font-weight: 700;
`;

const PricingFooterActions = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  flex: 0 0 auto;
  flex-wrap: nowrap;

  @media (max-width: 575px) {
    align-items: stretch;
    flex-wrap: wrap;
    width: 100%;
  }
`;

const TotalGeneralButton = styled.div`
  && {
    height: auto;
    align-items: center;
    display: flex;
    gap: 20px;
    justify-content: space-between;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 14px 18px;
    width: fit-content;
    margin-left: auto;
    min-width: 380px;
    max-width: 430px;
    background-color: #ffffff;
    color: #1a1a1a;
    box-shadow: none;
    cursor: default;
    white-space: normal;
  }

  &&:hover {
    border-color: #012443;
    color: #1a1a1a;
    background-color: #ffffff;
  }

  ${TotalMetricGroup}:not(:only-child):last-child {
    justify-items: end;
    text-align: right;
  }

  @media (max-width: 575px) {
    && {
      align-items: flex-start;
      flex-direction: column;
      gap: 16px;
      margin-left: 0;
      min-width: 0;
      max-width: 100%;
      padding: 14px 16px;
    }

    ${TotalMetricValue} {
      font-size: 18px;
    }

    ${TotalMetricGroup}:not(:only-child):last-child {
      justify-items: start;
      text-align: left;
    }
  }
`;

const TotalGeneralButton2 = styled(Button)`
  && {
    height: 40px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 0 18px;
    width: auto;
    min-width: 190px;
    max-width: none;
    background-color: #012443;
    color: #ffffff;
    box-shadow: none;
    flex: 0 0 auto;
  }

  &&:hover {
    border-color: #012443;
    color: #012443;
    background-color: #ffffff;
  }
`;

export const PRICING_TYPE_TOTAL = "Total";
export const PRICING_TYPE_TON = "Ton";
const PRODUCT_TABLE_ROW_LOCKED_FIELD = "__productTableRowLocked";

function markProductRowLocked(row = {}, locked = true) {
  return {
    ...row,
    [PRODUCT_TABLE_ROW_LOCKED_FIELD]: locked,
  };
}

function stripProductTableRowLock(row = {}) {
  const { [PRODUCT_TABLE_ROW_LOCKED_FIELD]: _locked, ...cleanRow } = row;
  return cleanRow;
}

function stripProductTableRowLocks(products = []) {
  return Array.isArray(products) ? products.map(stripProductTableRowLock) : [];
}

function areProductRowsEquivalent(left = [], right = []) {
  return (
    JSON.stringify(normalizePricingProducts(stripProductTableRowLocks(left))) ===
    JSON.stringify(normalizePricingProducts(stripProductTableRowLocks(right)))
  );
}

function isProductRowLocked(row = {}) {
  return row?.[PRODUCT_TABLE_ROW_LOCKED_FIELD] === true;
}

export function canChangeProductRow(row = {}) {
  return !isProductRowLocked(row);
}

export function parsePricingNumericValue(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const cleaned = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "");
  const normalized = cleaned.includes(",")
    ? cleaned.replace(/\./g, "").replace(",", ".")
    : cleaned;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

export function isFreteProductRow(row = {}) {
  return (
    String(row?.nameProducts || "")
      .trim()
      .toLowerCase() === "frete"
  );
}

export function resolvePricingType(row = {}) {
  if (!isFreteProductRow(row)) return PRICING_TYPE_TOTAL;
  const type = String(row?.pricingType || PRICING_TYPE_TOTAL).trim();
  return type === PRICING_TYPE_TON ? PRICING_TYPE_TON : PRICING_TYPE_TOTAL;
}

export function calculatePricingRowTotal(row = {}) {
  const amount = parsePricingNumericValue(row.amount);
  const priceList = parsePricingNumericValue(row.priceList) || 0;
  const weight = Number.isFinite(amount) ? amount : 0;

  if (weight <= 0 || priceList <= 0) return 0;

  if (isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON) {
    return Number((weight * priceList).toFixed(2));
  }

  return Number((weight * priceList).toFixed(2));
}

export function calculatePriceListFromTotal(row = {}, targetTotal) {
  const amount = Number(parsePricingNumericValue(row.amount)) || 1;
  const total = Number(targetTotal) || 0;

  if (isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON) {
    return total / amount;
  }

  return Number((total / amount).toFixed(2));
}

export function getCargoWeightInTons(weightCargo, weightUnit) {
  const weight = parsePricingNumericValue(weightCargo);
  if (!Number.isFinite(weight) || weight <= 0) return "";

  const unit = String(weightUnit || "")
    .trim()
    .toLowerCase();

  debugger
  console.log("unit:", unit);
  if (unit === "kg") {
    return weight / 1000;
  } else if (unit === "ton") {
    return weight;
  }


}

export function resolveProductTabWeightFromPricingProducts(
  products = [],
  weightUnit,
) {
  if (!Array.isArray(products)) return undefined;

  const freightTonProduct = products.find(
    (row) =>
      isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON,
  );
  const freightWeightInTons = parsePricingNumericValue(
    freightTonProduct?.amount,
  );

  if (!Number.isFinite(freightWeightInTons) || freightWeightInTons <= 0) {
    return undefined;
  }

  return String(weightUnit || "")
    .trim()
    .toLowerCase() === "kg"
    ? Number((freightWeightInTons * 1000).toFixed(3))
    : freightWeightInTons;
}

export function resolveDisplayedTonUnitPrice(products = []) {
  if (!Array.isArray(products)) return undefined;

  const freightTonProduct = products.find(
    (row) =>
      isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON,
  );
  const unitPrice = parsePricingNumericValue(freightTonProduct?.priceList);

  return Number.isFinite(unitPrice) && unitPrice > 0 ? unitPrice : undefined;
}

export function normalizePricingProductRow(row = {}) {
  const amountValue = parsePricingNumericValue(row.amount);
  const amount = Number.isFinite(amountValue) ? amountValue : 0;
  const priceList = parsePricingNumericValue(row.priceList) || 0;
  const rowWithType = {
    ...row,
    pricingType: resolvePricingType(row),
  };
  const computedTotal = calculatePricingRowTotal(rowWithType);
  const explicitTotal = parsePricingNumericValue(row.total);
  const hasExplicitTotal = Number.isFinite(explicitTotal) && explicitTotal >= 0;
  const total =
    isFreteProductRow(rowWithType) &&
      resolvePricingType(rowWithType) === PRICING_TYPE_TON &&
      hasExplicitTotal
      ? explicitTotal
      : amount > 0 && priceList > 0
        ? computedTotal
        : hasExplicitTotal
          ? explicitTotal
          : 0;

  return {
    ...rowWithType,
    total,
    netPrice: total,
  };
}

export function normalizePricingProducts(products = []) {
  if (!Array.isArray(products)) return [];
  return products.map(normalizePricingProductRow);
}

export function sumPricingProductsTotal(products = []) {
  return normalizePricingProducts(products).reduce(
    (sum, prod) => sum + (parsePricingNumericValue(prod.total) || 0),
    0,
  );
}

/** Tolerância usada ao comparar frete x soma da precificação (centavos / float). */
export const PRICING_FREIGHT_VALUE_EPSILON = 0.005;

export function isFreightValueBelowProductsTotal(valueFreightage, products = []) {
  const freightValue = parsePricingNumericValue(valueFreightage);
  if (!Number.isFinite(freightValue)) return false;

  const productsTotal = sumPricingProductsTotal(products);
  return freightValue + PRICING_FREIGHT_VALUE_EPSILON < productsTotal;
}

export function syncPricingProductsWithFreightValue(
  products = [],
  valueFreightage,
) {
  const freightValue = parsePricingNumericValue(valueFreightage);
  const normalized = normalizePricingProducts(products);

  if (!Number.isFinite(freightValue) || freightValue < 0) {
    return normalized;
  }

  const currentSum = sumPricingProductsTotal(normalized);
  if (Math.abs(currentSum - freightValue) < PRICING_FREIGHT_VALUE_EPSILON) {
    return normalized;
  }

  const hasContent = (row) =>
    String(row?.nameProducts || "").trim() !== "" ||
    parsePricingNumericValue(row?.amount) > 0 ||
    parsePricingNumericValue(row?.priceList) > 0 ||
    parsePricingNumericValue(row?.total) > 0;

  const filledRows = normalized.filter(hasContent);

  if (filledRows.length === 0) {
    return [
      {
        nameProducts: "Frete",
        pricingType: PRICING_TYPE_TOTAL,
        amount: 1,
        priceList: freightValue,
        total: freightValue,
        netPrice: freightValue,
      },
    ];
  }

  if (filledRows.length === 1) {
    const row = filledRows[0];
    const isTonFrete =
      isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON;
    const amount = isTonFrete
      ? Number(parsePricingNumericValue(row.amount)) || 1
      : 1;
    const priceList = isTonFrete
      ? calculatePriceListFromTotal({ ...row, amount }, freightValue)
      : freightValue;

    return normalizePricingProducts([
      {
        ...row,
        amount,
        priceList,
        total: freightValue,
        netPrice: freightValue,
      },
    ]);
  }

  const freteIdx = normalized.findIndex(isFreteProductRow);
  const targetIdx = freteIdx >= 0 ? freteIdx : normalized.findIndex(hasContent);
  if (targetIdx === -1) return normalized;

  const othersSum = normalized.reduce(
    (sum, row, index) =>
      index === targetIdx ? sum : sum + (parsePricingNumericValue(row.total) || 0),
    0,
  );
  const targetTotal = Number((freightValue - othersSum).toFixed(2));
  const targetRow = normalized[targetIdx];
  const amount = parsePricingNumericValue(targetRow.amount) || 1;
  const priceList = calculatePriceListFromTotal(targetRow, targetTotal);

  return normalizePricingProducts(
    normalized.map((row, index) =>
      index === targetIdx
        ? {
          ...row,
          priceList,
          total: targetTotal,
          netPrice: targetTotal,
        }
        : row,
    ),
  );
}

export function extractSimulationFreightValue(simulationData = {}) {
  const freightData = simulationData?.freight || {};
  const candidates = [
    freightData?.valueFreightage,
    simulationData?.valueFreightage,
    freightData?.value,
    simulationData?.value,
    freightData?.total,
    simulationData?.total,
  ];

  for (const candidate of candidates) {
    const parsed = parsePricingNumericValue(candidate);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return undefined;
}

const ProductTable = ({
  weightUnit,
  weightCargo,
  product,
  onChange,
  unitPricePrecision = 2,
  generalTotalOverride,
  onGeneralTotalClick,
  calculateGrossFreightProposedByShipper,
  code,
  disabled = false,
  suggestPriceDisabled = false,
  lockLoadedRows = false,
}) => {
  const [products, setProducts] = useState([createProducts()]);
  const [productsData, setProductsData] = useState([]);

  const navigate = useNavigate();
  const user = useSelector(state => state.user);

  // Formatação para exibição (totais, etc.) sempre com 2 casas decimais
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "";

    let numberValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numberValue)) return "";

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue);
  };

  const roundUnitPrice = (value) => {
    if (value === "" || value === null || value === undefined) return value;
    const factor = 10 ** unitPricePrecision;
    return Math.round(Number(value) * factor) / factor;
  };

  const registeredProductNames = useMemo(
    () =>
      new Set(
        productsData
          .map((prod) => String(prod?.product || "").trim())
          .filter(Boolean),
      ),
    [productsData],
  );

  const productOptions = useMemo(
    () =>
      productsData.map((prod) => ({
        value: String(prod.product || "").trim(),
      })),
    [productsData],
  );

  function handleProductNameChange(index, value) {
    const row = products[index];
    if (isProductRowLocked(row)) return;

    const trimmed = String(value || "").trim();
    if (trimmed && !registeredProductNames.has(trimmed)) {
      return;
    }
    handleChangeProducts(index, "nameProducts", trimmed);
  }

  const getAmountLabel = (row) =>
    isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON
      ? "Peso Estimado (Ton)"
      : "Quantidade";

  const getPriceLabel = (row) =>
    isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON
      ? "Valor por Ton"
      : "Valor Unitário";

  const calculateTotals = (productRow) => {
    const amount = parsePricingNumericValue(productRow.amount);
    const pricingType = isFreteProductRow(productRow)
      ? resolvePricingType(productRow)
      : PRICING_TYPE_TOTAL;
    const total = calculatePricingRowTotal({
      ...productRow,
      pricingType,
    });

    return normalizePricingProductRow({
      ...productRow,
      pricingType,
      amount: Number.isFinite(amount) ? amount : productRow.amount,
      total,
    });
  };

  useEffect(() => {
    if (product && product.length > 0) {
      setProducts((currentProducts) => {
        if (areProductRowsEquivalent(currentProducts, product)) {
          return currentProducts;
        }

        const normalized = normalizePricingProducts(product);
        const unlockedIndexes = new Set(
          currentProducts
            .map((row, index) => (!isProductRowLocked(row) ? index : -1))
            .filter((index) => index >= 0),
        );

        const currentLooksEmpty =
          currentProducts.length <= 1 &&
          currentProducts.every((row) => {
            const hasName = String(row?.nameProducts || "").trim() !== "";
            const hasAmount = parsePricingNumericValue(row?.amount) > 0;
            const hasPrice = parsePricingNumericValue(row?.priceList) > 0;
            const hasTotal = parsePricingNumericValue(row?.total) > 0;
            return !hasName && !hasAmount && !hasPrice && !hasTotal;
          });

        const incomingHasContent = normalized.some((row) => {
          const hasName = String(row?.nameProducts || "").trim() !== "";
          const hasAmount = parsePricingNumericValue(row?.amount) > 0;
          const hasPrice = parsePricingNumericValue(row?.priceList) > 0;
          const hasTotal = parsePricingNumericValue(row?.total) > 0;
          return hasName || hasAmount || hasPrice || hasTotal;
        });

        // Carga inicial do frete: só trava as linhas existentes quando o valor
        // veio de cálculo (lockLoadedRows). Fretes manuais/legado ficam
        // editáveis na edição.
        const isInitialHydrate = currentLooksEmpty && incomingHasContent;

        return normalized.map((row, index) => {
          if (isInitialHydrate) {
            return markProductRowLocked(row, lockLoadedRows === true);
          }
          if (unlockedIndexes.has(index) || index >= currentProducts.length) {
            return markProductRowLocked(row, false);
          }
          return markProductRowLocked(row, true);
        });
      });
    }
  }, [product, lockLoadedRows]);

  useEffect(() => {
    const overrideValue = parsePricingNumericValue(generalTotalOverride);
    if (!Number.isFinite(overrideValue)) return;

    setProducts((currentProducts) => {
      const syncedProducts = syncPricingProductsWithFreightValue(
        currentProducts,
        overrideValue,
      ).map((row, index) =>
        isProductRowLocked(currentProducts[index])
          ? markProductRowLocked(row, true)
          : row,
      );

      if (areProductRowsEquivalent(currentProducts, syncedProducts)) {
        return currentProducts;
      }

      onChange && onChange(stripProductTableRowLocks(syncedProducts));
      return syncedProducts;
    });
  }, [generalTotalOverride]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const result = await fetchProductGetAll(user);
        if (result.success) {
          setProductsData(result.data);
        } else {
          message.error("Erro ao carregar os produtos. Tente novamente.");
        }
      } catch (error) {
        message.error("Erro ao carregar os produtos. Tente novamente.");
        console.error(error);
      }
    };
    if (user) {
      fetchProducts();
    }
  }, [user]);

  function createProducts() {
    return {
      nameProducts: "",
      pricingType: PRICING_TYPE_TOTAL,
      amount: "",
      priceList: "",
      total: "",
      netPrice: "",
      [PRODUCT_TABLE_ROW_LOCKED_FIELD]: false,
    };
  }

  function handleChangeProducts(index, field, value) {
    if (!canChangeProductRow(products[index], field)) return;

    const updatedProducts = [...products];
    const row = products[index];
    const isTonWeight =
      isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON;
    const normalizedValue =
      field === "priceList"
        ? isTonWeight
          ? parsePricingNumericValue(value)
          : roundUnitPrice(value)
        : field === "amount"
          ? value === "" || value === null || value === undefined
            ? ""
            : parsePricingNumericValue(value)
          : value;
    updatedProducts[index][field] = Number.isNaN(normalizedValue)
      ? value
      : normalizedValue;

    if (
      field === "pricingType" &&
      isFreteProductRow(updatedProducts[index])
    ) {
      let total = parsePricingNumericValue(updatedProducts[index].total);
      const overrideTotal = parsePricingNumericValue(generalTotalOverride);

      if (Number.isFinite(overrideTotal)) {
        const otherProductsTotal = updatedProducts.reduce(
          (sum, product, productIndex) =>
            productIndex === index
              ? sum
              : sum + (parsePricingNumericValue(product.total) || 0),
          0,
        );

        total = Number((overrideTotal - otherProductsTotal).toFixed(2));
        if (total < 0) total = 0;
      }

      updatedProducts[index].total = total;
      updatedProducts[index].netPrice = total;

      if (value === PRICING_TYPE_TON) {
        console.log("Calculating amount for Ton pricing type", weightCargo);
        const amount = getCargoWeightInTons(weightCargo, value);

        updatedProducts[index].amount = amount;

        if (amount && Number.isFinite(total) && total > 0) {
          updatedProducts[index].priceList = calculatePriceListFromTotal(
            updatedProducts[index],
            total,
          );
        }
      }

      if (value === PRICING_TYPE_TOTAL && Number.isFinite(total)) {
        updatedProducts[index].amount = 1;
        updatedProducts[index].priceList = total;
      }
    }

    if (field === "nameProducts") {
      const isFrete =
        String(normalizedValue || "")
          .trim()
          .toLowerCase() === "frete";
      updatedProducts[index].pricingType = isFrete
        ? updatedProducts[index].pricingType || PRICING_TYPE_TOTAL
        : PRICING_TYPE_TOTAL;
    }

    if (
      field === "amount" ||
      field === "priceList" ||
      field === "nameProducts" ||
      field === "pricingType"
    ) {
      updatedProducts[index] = calculateTotals(updatedProducts[index]);
    }
    setProducts(updatedProducts);
    onChange && onChange(stripProductTableRowLocks(updatedProducts));
  }

  function handleAddProduct() {
    if (disabled) return;
    const newProducts = [
      ...products,
      markProductRowLocked(createProducts(), false),
    ];
    setProducts(newProducts);
    onChange && onChange(stripProductTableRowLocks(newProducts));
  }

  function handleRemoveProduct(index) {
    if (disabled) return;
    const row = products[index];
    if (isProductRowLocked(row)) return;

    const updatedProducts = [...products];
    updatedProducts.splice(index, 1);
    setProducts(updatedProducts);
    onChange && onChange(stripProductTableRowLocks(updatedProducts));
  }

  const displayedGeneralTotal = (() => {
    const overrideValue = parsePricingNumericValue(generalTotalOverride);
    if (Number.isFinite(overrideValue)) {
      return overrideValue;
    }
    return sumPricingProductsTotal(products);
  })();


  const displayedTonUnitPrice = (() => {
    const overrideValue = parsePricingNumericValue(generalTotalOverride);
    const cargoWeightInTons = getCargoWeightInTons(weightCargo, weightUnit);

    if (Number.isFinite(overrideValue)) {
      return overrideValue;
    }

    const tonProductAmount = products.find(
      (row) => isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON,
    )?.amount;

    console.log("Ton Product Amount:", tonProductAmount);
    console.log("Cargo Weight in Tons:", cargoWeightInTons);

    const productsTotal = sumPricingProductsTotal(products);
    const divisor = tonProductAmount ?? cargoWeightInTons;

    return Number((productsTotal / divisor).toFixed(2));
  })();
  // const displayedTonUnitPrice = resolveDisplayedTonUnitPrice(products);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  function handleGeneralTotalClick() {
    if (disabled || suggestPriceDisabled) return;
    setOpenSuccessModal(true);
  }

  const handleModalResult = (value) => {
    if (!value) return;
    setOpenSuccessModal(false);
    if (typeof onGeneralTotalClick === "function") {
      onGeneralTotalClick(value);
    }
  };

  const handleModalClose = () => {
    setOpenSuccessModal(false);
  };


  const valueByTon = products.find((row) => isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON)?.amount || getCargoWeightInTons(weightCargo, weightUnit);

  return (
    <ProductTableShell style={productTableThemeVars}>
      <Table
        className="product-pricing-table"
        pagination={false}
        dataSource={products}
        rowKey={(record, index) => index}
        scroll={{ x: 980 }}
        tableLayout="fixed"
      >
        <Table.Column
          title="Nome do Produto"
          dataIndex="nameProducts"
          key="nameProducts"
          width="32%"
          render={(text, record, index) => (
            <ProductFieldCell>
              <ProductFieldMeta />
              {disabled || isProductRowLocked(record) ? (
                <Input
                  value={record.nameProducts}
                  placeholder="Busque e selecione o produto"
                  disabled
                />
              ) : (
                <AutoComplete
                  style={{ width: "100%" }}
                  options={productOptions}
                  value={record.nameProducts}
                  placeholder="Busque e selecione o produto"
                  filterOption={(inputValue, option) =>
                    option.value.toUpperCase().includes(inputValue.toUpperCase())
                  }
                  onSelect={(value) => handleProductNameChange(index, value)}
                  onChange={(value) => handleProductNameChange(index, value)}
                  onBlur={() => {
                    if (
                      record.nameProducts &&
                      !registeredProductNames.has(
                        String(record.nameProducts).trim(),
                      )
                    ) {
                      handleChangeProducts(index, "nameProducts", "");
                    }
                  }}
                >
                  <Input />
                </AutoComplete>
              )}
            </ProductFieldCell>
          )}
        />
        <Table.Column
          title="Tipo"
          key="pricingType"
          width={140}
          render={(_, record, index) =>
            isFreteProductRow(record) ? (
              <ProductFieldCell>
                <ProductFieldMeta />
                <Select
                  style={{ width: "100%" }}
                  value={resolvePricingType(record)}
                  disabled={disabled || isProductRowLocked(record)}
                  options={[
                    { value: PRICING_TYPE_TOTAL, label: "Total" },
                    { value: PRICING_TYPE_TON, label: "Ton" },
                  ]}
                  onChange={(value) =>
                    handleChangeProducts(index, "pricingType", value)
                  }
                />
              </ProductFieldCell>
            ) : null
          }
        />
        <Table.Column
          title="Quantidade"
          dataIndex="amount"
          key="amount"
          width={210}
          render={(text, record, index) => {
            const isTonWeight =
              isFreteProductRow(record) &&
              resolvePricingType(record) === PRICING_TYPE_TON;

            return (
              <ProductFieldCell>
                <ProductFieldMeta>
                  {isTonWeight ? getAmountLabel(record) : ""}
                </ProductFieldMeta>
                {isTonWeight ? (
                  <IntlCurrencyInput
                    currency="BRL"
                    config={weightConfig}
                    className="form-control"
                    value={record.amount ?? ""}
                    disabled={disabled || isProductRowLocked(record)}
                    onChange={(event, value) =>
                      handleChangeProducts(index, "amount", value)
                    }
                  />
                ) : (
                  <InputNumber
                    min={0}
                    precision={2}
                    step={0.01}
                    decimalSeparator=","
                    value={record.amount}
                    disabled={disabled || isProductRowLocked(record)}
                    onChange={(value) =>
                      handleChangeProducts(index, "amount", value)
                    }
                  />
                )}
              </ProductFieldCell>
            );
          }}
        />
        <Table.Column
          title="Valor Unitário"
          dataIndex="priceList"
          key="priceList"
          width={210}
          render={(text, record, index) => (
            <ProductFieldCell>
              <ProductFieldMeta>
                {isFreteProductRow(record) &&
                  resolvePricingType(record) === PRICING_TYPE_TON
                  ? getPriceLabel(record)
                  : ""}
              </ProductFieldMeta>
              <IntlCurrencyInput
                currency="BRL"
                config={currencyConfig}
                className="form-control"
                value={record.priceList ?? ""}
                disabled={disabled || isProductRowLocked(record)}
                onChange={(event, value) =>
                  handleChangeProducts(index, "priceList", value)
                }
              />
            </ProductFieldCell>
          )}
        />
        <Table.Column
          title="Valor Total"
          dataIndex="total"
          key="total"
          width={140}
          render={(text, record, index) => (
            <ProductFieldCell>
              <ProductFieldMeta />
              <ProductTotalValue>
                {formatCurrency(record.total)}
              </ProductTotalValue>
            </ProductFieldCell>
          )}
        />
        <Table.Column
          title="Ações"
          key="actions"
          width={90}
          render={(text, record, index) => (
            <ProductFieldCell>
              <ProductFieldMeta />
              {!disabled && !isProductRowLocked(record) ? (
                <Button
                  type="danger"
                  onClick={() => handleRemoveProduct(index)}
                  style={{ color: "red" }}
                  title="Remover produto"
                >
                  <DeleteOutlinedIcon />
                </Button>
              ) : null}
            </ProductFieldCell>
          )}
        />
      </Table>
      <Box
        sx={{
          marginTop: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <PricingFooterActions>
          <StyledButton
            type="primary"
            onClick={handleAddProduct}
            disabled={disabled}
          >
            Adicionar Produto
          </StyledButton>


          {code < 5 && (
            <TotalGeneralButton2
              htmlType="button"
              onClick={handleGeneralTotalClick}
              disabled={disabled || suggestPriceDisabled}
            >
              {/* Seção Esquerda: Radio e Títulos */}
              <Box sx={{ alignItems: "center", display: "flex" }}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", lineHeight: 1.2, }}
                  >
                    Sugerir Preço
                  </Typography>
                </Box>
              </Box>

            </TotalGeneralButton2>
          )}
        </PricingFooterActions>

        <TotalGeneralButton>
          {/* Seção Esquerda: Radio e Títulos */}
          <TotalMetricGroup>
            <TotalMetricLabel>Total Geral</TotalMetricLabel>
            <TotalMetricValue>
              {formatCurrency(displayedGeneralTotal)}
            </TotalMetricValue>
          </TotalMetricGroup>

          {/* Seção Direita: Preço e Período */}
          {displayedTonUnitPrice !== undefined && (
            <TotalMetricGroup>
              <TotalMetricLabel>Valor por Tonelada</TotalMetricLabel>
              <TotalMetricValueSecondary>
                {formatCurrency(displayedTonUnitPrice)}/Ton
              </TotalMetricValueSecondary>
            </TotalMetricGroup>
          )}
        </TotalGeneralButton>


      </Box>

      <FreightEditemodSuccessModaTest
        open={openSuccessModal}
        handleClose={handleModalClose}
        onResult={handleModalResult}
        dataMsg={{
          oldValue: displayedGeneralTotal,
          byTheTon: displayedTonUnitPrice,
        }}
        calculateGrossFreightProposedByShipper={
          calculateGrossFreightProposedByShipper
        }
        valueByTon={valueByTon}
      />
    </ProductTableShell>
  );
};

export default ProductTable;
